import { ref, watch } from "vue";
import { defineStore } from "pinia";
import { SidebarMenuItem } from "@/types/navigation";
import { CreditSummary, Organization, OrganizationDetailsOptions, OrganizationWithDetails, SearchOrganizationsResponse, SearchUsersResponse } from "@/types/organization";
import { buildQueryString, FetchResponse, fetchWithNullHandling } from "../lib/fetch";
import { OrganizationFormData } from "@/components/OrganizationDetailsForm.vue";
import { Pagination, PaginationResponse, defaultPagination } from "@/types/pagination";
import { OrganizationCreditType, ReclaimFromStrategy } from "../types/credits";
import { User, UserOrganizationsRoles, RemoveUserResponse } from "../types/user";
import { AddUserFormData } from "@/components/modals/AddUserModal.vue";
import { UserFacingRoleNames } from "@/types/uifeatures";
import { CSVUserTableItem } from "@/views/UsersView.vue";
import { License, LicenseIdAndAmount, LicenseType } from "@/types/license";
import { AdoptionRequest, AdoptionRequestStatus, GetAdoptionRequestsResponse } from "@/types/adoptionRequest";
import { useUserStore } from "./user.store";
import { PermissionName, Role } from "@/types/permission";

export const useAppStore = defineStore("appStore", () => {
  const selectedNavMenuItem = ref(SidebarMenuItem.Home);
  const isLoadingOrganizations = ref(false);
  const subOrganizations = ref<OrganizationWithDetails[]>([]);
  const parentOrganizations = ref<Organization[]>([]);
  const subOrgPagination = ref<Pagination>(defaultPagination());
  const userPagination = ref<Pagination>(defaultPagination());
  const selectedOrganization = ref<OrganizationWithDetails | null>(null);
  const isLoadingUsers = ref(false);
  const users = ref<User[]>([]);
  const incomingAdoptionRequests = ref<AdoptionRequest[]>([]);
  const outgoingAdoptionRequests = ref<AdoptionRequest[]>([]);
  const isProcessingAdoption = ref(false);
  const integrationOAuthCallback = ref<{ integration: string; status: string; message?: string; errorShown?: boolean } | null>(null);

  // Non-reactive data
  const userStore = useUserStore();

  watch(selectedOrganization, (newVal, oldVal) => {
    if(newVal !== oldVal) {
      userStore.setPermissionsAndRole(newVal?.permissionsAndRole);
      getAdoptionRequests();
    }
  });

  /**
   * Fetch your organization and direct sub-organizations, or all root organizations if you are a system admin.
   */
  async function fetchUserOrganization(): Promise<OrganizationWithDetails[] | undefined> {
    try {
      subOrganizations.value = [];
      selectedOrganization.value = null;

      isLoadingOrganizations.value = true;
      const response = await fetchWithNullHandling<OrganizationWithDetails | OrganizationWithDetails[]>("/api/organization");
      isLoadingOrganizations.value = false;
      if(response.status !== 200 || !response.data) {
        console.error("Failed to fetch user organization", response.status, response.statusText, response.dataText);
        return;
      }
      // System admins get all root organizations
      if(Array.isArray(response.data)) {
        // If only 1 root organization, put it in the tree and fetch its sub-organizations directly
        if(response.data.length === 1) {
          const organization: OrganizationWithDetails = response.data[0];
          selectedOrganization.value = organization;
          fetchSubOrganizations(organization.id, { licenses: true, subOrganizations: true, credits: true, owner: true });
          return [organization];
        }
        // Otherwise make them 'sub-organizations', user have to select one
        else {
          subOrganizations.value = response.data;
          subOrgPagination.value = { ...subOrgPagination.value, limit: 50, page: 1 };
        }
      }
      // User gets their own organization
      else {
        const organization: OrganizationWithDetails = response.data;
        if(organization && organization.id) {
          selectedOrganization.value = organization;
          fetchSubOrganizations(organization.id, { licenses: true, subOrganizations: true, credits: true, owner: true });
          return [organization];
        }
      }
    }
    catch(error) {
      console.error("Failed to fetch user organization", error);
      isLoadingOrganizations.value = false;
    }
  }

  async function loadMoreSubOrganizations(inOrganizationId: number, inOptions: OrganizationDetailsOptions, inAppend: boolean) {
    subOrgPagination.value.page += 1;
    if(selectedOrganization.value?.id) {
      await fetchSubOrganizations(selectedOrganization.value.id, inOptions, inAppend);
    }
  }

  async function fetchSubOrganizations(inOrganizationId: string, inOptions?: OrganizationDetailsOptions, inAppend: boolean = false) {
    try {
      isLoadingOrganizations.value = true;

      // Map frontend sort keys to backend sort keys
      let sortKey = subOrgPagination.value?.key;
      if(sortKey === "licensesUsed") {
        sortKey = "users.max";
      }
      else if(sortKey === "owner") {
        sortKey = "owner.email";
      }

      const params = {
        options: JSON.stringify(inOptions),
        page: subOrgPagination.value?.page ?? 1,
        limit: subOrgPagination.value?.limit ?? 20,
        search: subOrgPagination.value?.search ?? "",
        key: sortKey,
        order: subOrgPagination.value?.order
      };
      const query = buildQueryString(params);
      const url = `/api/organization/${inOrganizationId}/sub-organizations?${query}`;

      const response = await fetchWithNullHandling<PaginationResponse<OrganizationWithDetails[]>>(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });

      isLoadingOrganizations.value = false;
      if(response.status === 200) {
        const paginationResponse = response.data as PaginationResponse<OrganizationWithDetails[]>;

        if(inAppend) {
          subOrganizations.value.push(...paginationResponse.data);
        }
        else {
          subOrganizations.value = paginationResponse.data;
        }

        // Map backend sort keys back to frontend sort keys for UI consistency
        const pagination = { ...paginationResponse.pagination };
        if(pagination.key === "users.max") {
          pagination.key = "licensesUsed";
        }
        else if(pagination.key === "owner.email") {
          pagination.key = "owner";
        }
        subOrgPagination.value = pagination;
      }
      else {
        console.error("Failed to fetch sub organizations", response.status, response.statusText);
      }
    }
    catch(error) {
      console.error("Failed to fetch sub organizations", error);
      isLoadingOrganizations.value = false;
    }
  }

  async function fetchParentOrganizations(): Promise<void> {
    if(!selectedOrganization.value?.id) {
      console.error("No organization ID to fetch parent organizations for");
      return;
    }
    try {
      const response = await fetchWithNullHandling<Organization[]>(`/api/organization/${selectedOrganization.value.id}/parent-organizations`);
      parentOrganizations.value = response.data ?? [];
    }
    catch(error) {
      console.error("Failed to fetch parent organizations", error);
    }
  }

  async function fetchOrganizationCreditsSummary(): Promise<void> {
    if(!selectedOrganization.value?.id) {
      console.error("No organization ID to fetch details");
      return;
    }
    try {
      const response = await fetchWithNullHandling<CreditSummary>(`/api/organization/${selectedOrganization.value.id}/credits`);
      if(response.status !== 200 || !response.data) {
        console.error("Failed to fetch organization credits summary", response.status, response.data);
        return;
      }
      selectedOrganization.value.credits = response.data;
    }
    catch(error) {
      console.error("Failed to fetch organizations details", error);
    }
  }

  async function addOrganization(inData: OrganizationFormData) {
    try {
      if(!selectedOrganization.value) {
        console.error("No selected organization to add sub-organization to");
        return;
      }

      const data = {
        name: inData.name,
        phoneNumber: inData.phone,
        type: inData.type,
        parentOrganizationId: selectedOrganization.value.id,
        allowDataSharing: inData.allowDataSharing
      };
      const response = await fetchWithNullHandling<OrganizationWithDetails>("/api/organization/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(data)
      });

      if(response.status !== 200 || !response.data) {
        console.error("Error creating organization", response.status, response.data);
        return;
      }
      subOrganizations.value.push(response.data);

      return response?.data && response?.data?.id ? response.data.id : null;
    }
    catch(error) {
      console.error("Failed to add organization", error);
    }
    return null;
  }

  async function selectAndFetchOrganization(id: string) {
    const options: OrganizationDetailsOptions = {
      licenses: true,
      subOrganizations: true,
      credits: true,
      permissionsAndRole: true
    };
    const response = await fetchWithNullHandling<OrganizationWithDetails>(`/api/organization/${id}?options=${JSON.stringify(options)}`);
    selectedOrganization.value = response.data;
  }

  async function searchOrganizations(inPagination: Pagination): Promise<PaginationResponse<SearchOrganizationsResponse> | undefined> {
    isLoadingOrganizations.value = true;

    const params = {
      page: inPagination.page ?? 1,
      limit: inPagination.limit ?? 20,
      search: inPagination.search ?? "",
      key: inPagination.key,
      order: inPagination.order
    };

    const query = buildQueryString(params);
    const url = `/api/organization/search?${query}`;

    const response = await fetchWithNullHandling<PaginationResponse<SearchOrganizationsResponse>>(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    });
    isLoadingOrganizations.value = false;
    if(response.status !== 200 || !response.data) {
      console.error("Failed to search for organizations", response.status, response.statusText);
      return;
    }
    return response.data;
  }

  async function searchUsers(inPagination: Pagination): Promise<PaginationResponse<SearchUsersResponse> | undefined> {
    isLoadingUsers.value = true;

    const params = {
      page: inPagination.page ?? 1,
      limit: inPagination.limit ?? 20,
      search: inPagination.search ?? "",
      key: inPagination.key,
      order: inPagination.order
    };

    const query = buildQueryString(params);
    const url = `/api/user/search?${query}`;

    const response = await fetchWithNullHandling<PaginationResponse<SearchUsersResponse>>(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    });
    isLoadingUsers.value = false;
    if(response.status !== 200 || !response.data) {
      console.error("Failed to search for users", response.status, response.statusText);
      return;
    }
    return response.data;
  }

  async function updateSelectedOrganization(inData: Partial<Pick<Organization, "name" | "type" | "phoneNumber" | "parentOrganizationId" | "allowDataSharing">>) {
    if(!selectedOrganization.value) {
      console.error("No selected organization to update");
      return;
    }

    try {
      const response = await fetchWithNullHandling<OrganizationWithDetails>(`/api/organization/${selectedOrganization.value.id}/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: inData.name,
          type: inData.type,
          phoneNumber: inData.phoneNumber,
          allowDataSharing: inData.allowDataSharing
        })
      });
      if(response.status !== 200 || !response.data) {
        console.error("Failed to update selected organization", response.status, response.data);
        return;
      }
      selectedOrganization.value = response.data;
    }
    catch(error) {
      console.error("Failed to update selected organization", error);
    }
  }

  async function assignCreditsToOrgs(inAmount: number, inSourceOrganizationId: string, inFromOrganizationPoolType: OrganizationCreditType, inTargetOrganizationIds: string[], inToOrganizationPoolType: OrganizationCreditType): Promise<FetchResponse<unknown>> {
    return await fetchWithNullHandling("/api/credit/assign/organizations", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        amount: inAmount,
        sourceOrganizationId: inSourceOrganizationId,
        fromOrganizationPoolType: inFromOrganizationPoolType,
        targetOrganizationIds: inTargetOrganizationIds,
        toOrganizationPoolType: inToOrganizationPoolType
      })
    });
  }

  async function reclaimCreditsFromOrgs(inAmount: number, inSourceOrganizationIds: string[], inReclaimFromStrategy: ReclaimFromStrategy, inTargetOrganizationId: string, inToOrganizationPoolType: OrganizationCreditType): Promise<FetchResponse<unknown>> {
    return await fetchWithNullHandling("/api/credit/reclaim/organizations", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        amount: inAmount,
        sourceOrganizationIds: inSourceOrganizationIds,
        reclaimFromStrategy: inReclaimFromStrategy,
        targetOrganizationId: inTargetOrganizationId,
        toOrganizationPoolType: inToOrganizationPoolType
      })
    });
  }

  async function transferCreditsBetweenOrgs(inAmount: number, inSourceOrganizationId: string, inFromOrganizationPoolType: OrganizationCreditType, inTargetOrganizationId: string, inToOrganizationPoolType: OrganizationCreditType): Promise<FetchResponse<unknown>> {
    return await fetchWithNullHandling("/api/credit/transfer/organizations", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        amount: inAmount,
        sourceOrganizationId: inSourceOrganizationId,
        fromOrganizationPoolType: inFromOrganizationPoolType,
        targetOrganizationId: inTargetOrganizationId,
        toOrganizationPoolType: inToOrganizationPoolType
      })
    });
  }

  async function transferCreditsBetweenPools(inAmount: number, inOrganizationId: string, inFromPool: OrganizationCreditType, inToPool: OrganizationCreditType): Promise<FetchResponse<unknown>> {
    return await fetchWithNullHandling("/api/credit/transfer/pools", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        amount: inAmount,
        organizationId: inOrganizationId,
        fromPool: inFromPool,
        toPool: inToPool
      })
    });
  }

  async function allocateCreditsToUsers(inAmount: number, inUserIds: string[], inOrganizationId: string, inFromOrganizationPoolType: OrganizationCreditType): Promise<FetchResponse<unknown>> {
    return await fetchWithNullHandling("/api/credit/assign/users", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        amount: inAmount,
        userIds: inUserIds,
        organizationId: inOrganizationId,
        fromOrganizationPoolType: inFromOrganizationPoolType
      })
    });
  }

  async function reclaimCreditsFromUsers(inAmount: number, inUserIds: string[], inOrganizationId: string, inToOrganizationPoolType: OrganizationCreditType): Promise<FetchResponse<unknown>> {
    return await fetchWithNullHandling("/api/credit/reclaim/users", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        amount: inAmount,
        userIds: inUserIds,
        organizationId: inOrganizationId,
        toOrganizationPoolType: inToOrganizationPoolType
      })
    });
  }

  async function transferCreditsBetweenUsers(inAmount: number, inSourceUserId: string, inTargetUserId: string): Promise<FetchResponse<unknown>> {
    return await fetchWithNullHandling("/api/credit/transfer/users", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        amount: inAmount,
        sourceUserId: inSourceUserId,
        targetUserId: inTargetUserId
      })
    });
  }

  async function fetchUsersForOrganization(inOrganizationId: string) {
    isLoadingUsers.value = true;
    const params = {
      page: userPagination.value?.page ?? 1,
      limit: userPagination.value?.limit ?? 20,
      search: userPagination.value?.search ?? "",
      key: userPagination.value?.key,
      order: userPagination.value?.order,
      withCredits: true,
    };
    const queryString = buildQueryString(params);

    const response = await fetchWithNullHandling<{ data: User[], pagination: Pagination }>(`/api/organization/${inOrganizationId}/users?${queryString}`);
    isLoadingUsers.value = false;
    if(response.status !== 200 || !response.data) {
      console.error("Failed to fetch users for organization", response.status, response.statusText);
      return;
    }
    users.value = response.data.data;
    userPagination.value = response.data.pagination;
  }

  async function updateUserRole(inUserId: string, inRole: Role) {
    if(!selectedOrganization.value?.id) {
      console.error("No selected organization to update user role for");
      return;
    }
    const user = users.value.find(user => user.id === inUserId);
    if(!user) {
      console.error("No user to update role for");
      return;
    }

    const response = await fetchWithNullHandling<UserOrganizationsRoles[]>(`/api/user/${inUserId}/update/role`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ role: inRole, organizationId: selectedOrganization.value.id })
    });

    if(!response.ok) {
      console.error("Failed to update user role", response.status, response.data);
      return;
    }
    // Update user list with response data
    for(const userRole of response.data ?? []) {
      const updatedUser = users.value.find(user => user.id === userRole.userId);
      if(updatedUser && userRole.organizationId === selectedOrganization.value?.id) {
        updatedUser.role = userRole.roleName ?? updatedUser.role;
      }
    }
  }

  async function updateUserData(inUserId: string, inData: Partial<User>) {
    const user = users.value.find(user => user.id === inUserId);
    if(!user) {
      console.error("No user to update data for");
      return;
    }

    // Only include data that is relevant to user table
    const userData = {
      name: inData.name,
      email: inData.email,
      locale: inData.locale
    };

    const response = await fetchWithNullHandling<User>(`/api/user/${inUserId}/update`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(userData)
    });

    if(!response.ok) {
      console.error("Failed to update user data", response.status, response.data);
      return;
    }
    const updatedUser = response.data;
    user.name = updatedUser?.name ?? user.name;
    user.email = updatedUser?.email ?? user.email;
    user.locale = updatedUser?.locale ?? user.locale;
  }

  async function updateUserLicenses(inUserIds: string[], inLicenseId: number | null) {
    if(!selectedOrganization.value?.id) {
      console.error("No selected organization to update user license for");
      return;
    }
    if(inUserIds.length === 0) {
      console.error("No users to update license for");
      return;
    }

    const response = await fetchWithNullHandling<number>("/api/user/update/licenses", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ userIds: inUserIds, licenseId: inLicenseId, organizationId: selectedOrganization.value.id })
    });

    if(!response.ok) {
      console.error("Failed to update user license", response.status, response.data);
    }
  }

  async function addUsers(inUsers: CSVUserTableItem[], inSendCredentialsEmail = false) {
    // Only allowing unique emails
    const uniqueEmails = new Set();
    const uniqueUsers = inUsers.filter((user) => {
      if(uniqueEmails.has(user.email)) {
        return false;
      }
      else {
        uniqueEmails.add(user.email);
        return true;
      }
    });
    const users = uniqueUsers.map((user) => {
      const userRole = user.user_role as keyof typeof UserFacingRoleNames;
      const mappedRole = UserFacingRoleNames[userRole];
      if(!mappedRole) {
        console.error(`${user.user_role} role is not allowed`);
        return;
      }
      return {
        email: user.email,
        name: user.user_name,
        role: mappedRole
      };
    }).filter(Boolean);
    const requestBody = {
      organizationId: selectedOrganization.value?.id,
      users: users,
      sendCredentialsEmail: inSendCredentialsEmail
    };
    const response = await fetchWithNullHandling<User[]>("/api/user/bulk", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(requestBody)
    });
    if(response.status === 402) {
      return {
        data: [],
        limitReached: true,
        error: true
      };
    }
    if(response.status === 500) {
      return {
        data: [],
        limitReached: false,
        error: true,
      };
    }
    return {
      data: response.data,
      limitReached: false,
      error: false
    };
  }

  async function addUser(inUserData: AddUserFormData): Promise<{ unique: boolean, created: boolean }> {
    isLoadingUsers.value = true;
    const params = {
      email: inUserData.email,
      name: inUserData.name,
      organizationId: selectedOrganization.value?.id,
      role: inUserData.role
    };
    const response = await fetchWithNullHandling<User>("/api/user/create", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(params)
    });

    if(response.status === 409) {
      isLoadingUsers.value = false;
      return { unique: false, created: false };
    }

    isLoadingUsers.value = false;

    if(response.status !== 200 || !response.data) {
      console.error("Error creating user", response.status, response.data);
      return { unique: true, created: false };
    }
    // Refresh user list
    await fetchUsersForOrganization(selectedOrganization.value?.id ?? "");
    return { unique: true, created: true };
  }

  async function removeUsersFromOrganization(inUserIds: string[], inOrganizationId: string): Promise<boolean> {
    isLoadingUsers.value = true;

    const params = {
      organizationId: inOrganizationId,
      userIds: inUserIds
    };

    const response = await fetchWithNullHandling<Array<RemoveUserResponse>>("/api/user/remove", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(params)
    });

    if(response.data) {
      const removedUserIds: Set<string> = new Set(response.data
        .filter(res => res.removed)
        .map(res => res.userId));

      users.value = users.value.filter(user => !removedUserIds.has(user.id));
    }

    isLoadingUsers.value = false;

    return !!response.data;
  }

  async function deleteOrganization(inOrganizationId: string): Promise<{ result: boolean, message: string }> {
    isLoadingOrganizations.value = true;

    const response = await fetchWithNullHandling<{ result?: boolean, message?: string }>(`/api/organization/${inOrganizationId}/delete`, {
      method: "DELETE"
    });

    if(response.ok && response.data?.result) {
      subOrganizations.value.splice(subOrganizations.value.findIndex(o => o.id === inOrganizationId), 1);
      await fetchOrganizationCreditsSummary();
    }

    isLoadingOrganizations.value = false;

    if(response.ok && response.data?.result) {
      return { result: true, message: response.data.message ?? "organization_delete_success" };
    }

    if(response.status === 403) {
      return { result: false, message: "organization_delete_unauthorized" };
    }

    if(response.data?.message) {
      return { result: false, message: response.data.message };
    }

    return { result: false, message: "organization_delete_fail" };
  }

  async function assignLicensesToOrganizations(inSourceOrganizationId: string, inTargetOrganizationIds: string[], inLicenses: LicenseIdAndAmount[]): Promise<FetchResponse<unknown>> {
    return await fetchWithNullHandling("/api/license/assign/organizations", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        sourceOrganizationId: inSourceOrganizationId,
        targetOrganizationIds: inTargetOrganizationIds,
        licenses: inLicenses.filter(l => l.amount > 0)
      })
    });
  }

  async function reclaimLicensesToOrganizations(inSourceOrganizationIds: string[], inTargetOrganizationId: string, inLicenses: LicenseIdAndAmount[]): Promise<FetchResponse<unknown>> {
    return await fetchWithNullHandling("/api/license/reclaim/organizations", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        sourceOrganizationIds: inSourceOrganizationIds,
        targetOrganizationId: inTargetOrganizationId,
        licenses: inLicenses.filter(l => l.amount > 0)
      })
    });
  }

  async function createLicense(
    inOrganizationId: string,
    inLicenseType: LicenseType,
    inMaxUsers: number,
    inExpireAt: Date | null
  ): Promise<FetchResponse<License[]> | null> {
    if(!userStore.isSystemAdmin) {
      console.error("User is not a system admin");
      return null;
    }
    if(inMaxUsers <= 0 || !inOrganizationId) {
      console.error("Invalid max users or organization ID");
      return null;
    }
    return await fetchWithNullHandling<License[]>("/api/license/create", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        organizationId: inOrganizationId,
        licenseType: inLicenseType,
        expireAt: inExpireAt,
        maxUsers: inMaxUsers
      })
    });
  }

  async function updateLicense(
    inLicenseId: number,
    inExpireAt?: Date | null,
    inMaxUsers?: number,
    inLicenseType?: LicenseType,
    inMetadata?: Record<string, unknown>
  ): Promise<License | void> {
    if(!userStore.isSystemAdmin) {
      console.error("User is not a system admin");
      return;
    }
    if(inMaxUsers !== undefined && inMaxUsers < 0) {
      console.error("Invalid max users");
      return;
    }
    const response = await fetchWithNullHandling<License>(`/api/license/${inLicenseId}/update`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        licenseId: inLicenseId,
        licenseType: inLicenseType,
        expireAt: inExpireAt,
        maxUsers: inMaxUsers,
        metadata: inMetadata
      })
    });
    if(response.status !== 200 || !response.data) {
      console.error("Error updating license", response.status, response.data);
      return;
    }
    return response.data;
  }

  async function deleteLicense(inLicenseId: number): Promise<boolean> {
    if(!userStore.isSystemAdmin) {
      console.error("User is not a system admin");
      return false;
    }
    if(inLicenseId < 0) {
      console.error("Invalid license ID");
      return false;
    }
    const response = await fetchWithNullHandling<License>(`/api/license/${inLicenseId}/delete`, {
      method: "DELETE"
    });
    return response.ok;
  }

  type AdoptionRequestResult = { success: boolean; errorMessage?: string };
  type AdoptionRequestError = { message: string; code?: string };
  async function createAdoptionRequest(inOrganizationId: string, inTargetEmail: string, inTargetOrgId: string): Promise<AdoptionRequestResult> {
    if(!inOrganizationId || (!inTargetEmail && !inTargetOrgId)) {
      return { success: false, errorMessage: "Invalid parameters" };
    }
    // Check permission to create adoption requests
    if(!userStore.hasPermission(PermissionName.SendAdoptionRequest)) {
      return { success: false, errorMessage: "" };
    }

    interface AdoptionRequestPayload {
      organizationId: string;
      targetUserEmail?: string;
      targetOrganizationId?: string;
    }

    const payload: AdoptionRequestPayload = {
      organizationId: inOrganizationId,
    };
    if(inTargetEmail) {
      payload.targetUserEmail = inTargetEmail;
    }
    if(inTargetOrgId) {
      payload.targetOrganizationId = inTargetOrgId;
    }

    const response = await fetchWithNullHandling<AdoptionRequest | AdoptionRequestError>("/api/organization/adoption-requests/send", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    if(response.ok) {
      return { success: true };
    }

    // Extract error message from response
    const errorMessage = response.data && typeof response.data === "object" && "message" in response.data
      ? response.data.message : "";

    return { success: false, errorMessage };
  }

  async function processAdoptionRequest(inId: number, inAction: AdoptionRequestStatus): Promise<AdoptionRequestResult> {
    if(!selectedOrganization.value?.id) {
      return { success: false, errorMessage: "No organization selected" };
    }
    // Check permission to process adoption requests
    if(!userStore.hasPermission(PermissionName.ProcessAdoptionRequest) && !userStore.hasPermission(PermissionName.SendAdoptionRequest)) {
      return { success: false, errorMessage: "" };
    }

    isProcessingAdoption.value = true;
    const response = await fetchWithNullHandling<AdoptionRequest | AdoptionRequestError>("/api/organization/adoption-requests/process", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        organizationId: selectedOrganization.value.id,
        requestId: inId,
        action: inAction
      })
    });

    // Refresh the adoption requests
    await getAdoptionRequests();

    isProcessingAdoption.value = false;

    if(response.ok) {
      return { success: true };
    }

    const errorMessage = response.data && typeof response.data === "object" && "message" in response.data
      ? response.data.message : "";

    return { success: false, errorMessage };
  }

  async function getAdoptionRequests(): Promise<void> {
    if(!selectedOrganization.value?.id) {
      return;
    }
    // Check permission to view adoption requests
    if(!userStore.hasPermission(PermissionName.ViewAdoptionRequests)) {
      return;
    }

    const response =  await fetchWithNullHandling<GetAdoptionRequestsResponse>(`/api/organization/${selectedOrganization.value?.id}/adoption-requests`);

    if(response.status !== 200 || !response.data) {
      console.error("Error fetching adoption requests ", response.status, response.data);
      return;
    }
    incomingAdoptionRequests.value = response.data.incomingAdoptionRequests;
    outgoingAdoptionRequests.value = response.data.outgoingAdoptionRequests;
  }

  function setIntegrationOAuthCallback(inCallback: { integration: string; status: string; message?: string; errorShown?: boolean }) {
    integrationOAuthCallback.value = inCallback;
  }

  function clearIntegrationOAuthCallback() {
    integrationOAuthCallback.value = null;
  }

  async function transferUsersToOrganization(inUserIds: string[], inSourceOrgId: string, inTargetOrgId: string): Promise<boolean> {
    isLoadingUsers.value = true;
    if(!userStore.hasPermission(PermissionName.ManageUsers)) {
      console.error("User doesn't have enough permissions");
      return false;
    }

    if(!Array.isArray(inUserIds) || inUserIds.length === 0 || !inSourceOrgId || !inTargetOrgId) {
      console.error("Invalid arguments");
      return false;
    }

    const body = {
      sourceOrganizationId: inSourceOrgId,
      targetOrganizationId: inTargetOrgId,
      userIds: inUserIds
    };
    const response = await fetchWithNullHandling<{successful: boolean}>("/api/user/transfer", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    });

    if(selectedOrganization.value?.id) {
      await fetchUsersForOrganization(selectedOrganization.value?.id);
    }

    isLoadingUsers.value = false;

    return !!response.data;
  }

  async function fetchAndReturnCurrentUserOrganizations(inPagination: Partial<Pagination> = defaultPagination()): Promise<PaginationResponse<OrganizationWithDetails[]> | null> {
    const options = { owner: true };
    const queryParams = {
      ...inPagination,
      options: JSON.stringify(options)
    };
    const queryData = buildQueryString(queryParams);
    const res = await fetchWithNullHandling<PaginationResponse<OrganizationWithDetails[]>>(`/api/user/organizations?${queryData}`);

    if(!res.ok || !Array.isArray(res.data?.data)) {
      return null;
    }

    return res.data;
  }

  async function fetchAndReturnSuborganizations(inOrgId: string, inPagination: Partial<Pagination> = defaultPagination()): Promise<PaginationResponse<OrganizationWithDetails[]> | null> {
    if(!inOrgId) {
      return null;
    }

    const options = { owner: true };
    const queryParams = {
      ...inPagination,
      options: JSON.stringify(options)
    };
    const queryData = buildQueryString(queryParams);
    const res = await fetchWithNullHandling<PaginationResponse<OrganizationWithDetails[]>>(`/api/organization/${inOrgId}/sub-organizations?${queryData}`);

    if(!res.ok || !Array.isArray(res.data?.data)) {
      return null;
    }

    return res.data;
  }

  async function updateUserPassword(inUserId: string, inPassword: string): Promise<boolean> {
    try {
      if(!inUserId || !inPassword) {
        return false;
      }

      const r = await fetchWithNullHandling(`/api/user/${inUserId}/update/password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          password: inPassword
        })
      });

      if(r.error || r.status !== 200) {
        return false;
      }

      return true;
    }
    catch(err) {
      console.error("Unable to update user password", err);
      return false;
    }
  }

  return {
    selectedNavMenuItem,
    isLoadingOrganizations,
    subOrganizations,
    parentOrganizations,
    subOrgPagination,
    userPagination,
    selectedOrganization,
    isLoadingUsers,
    users,
    incomingAdoptionRequests,
    outgoingAdoptionRequests,
    isProcessingAdoption,
    fetchUserOrganization,
    loadMoreSubOrganizations,
    fetchSubOrganizations,
    fetchParentOrganizations,
    fetchOrganizationCreditsSummary,
    addOrganization,
    selectAndFetchOrganization,
    searchOrganizations,
    searchUsers,
    updateSelectedOrganization,
    assignCreditsToOrgs,
    reclaimCreditsFromOrgs,
    transferCreditsBetweenOrgs,
    transferCreditsBetweenPools,
    allocateCreditsToUsers,
    reclaimCreditsFromUsers,
    transferCreditsBetweenUsers,
    fetchUsersForOrganization,
    updateUserRole,
    updateUserData,
    updateUserLicenses,
    addUsers,
    addUser,
    removeUsersFromOrganization,
    deleteOrganization,
    assignLicensesToOrganizations,
    reclaimLicensesToOrganizations,
    createLicense,
    updateLicense,
    deleteLicense,
    transferUsersToOrganization,
    createAdoptionRequest,
    processAdoptionRequest,
    getAdoptionRequests,
    integrationOAuthCallback,
    setIntegrationOAuthCallback,
    clearIntegrationOAuthCallback,
    fetchAndReturnCurrentUserOrganizations,
    fetchAndReturnSuborganizations,
    updateUserPassword
  };
});
